import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaIdCard, FaSearch, FaEnvelope, FaPhone, FaMapMarkerAlt, FaLink, FaTint,
  FaCheckCircle, FaTimesCircle, FaRedo, FaChevronLeft, FaChevronRight, FaClock
} from "react-icons/fa";
import { toast } from "react-toastify";

const PendingIdCards = () => {
  const [idCards, setIdCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const fetchIdCards = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/icards`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const cardsData = res.data.reportersIdCard || res.data.data || res.data || [];

      if (Array.isArray(cardsData)) {
        setIdCards(cardsData);
      } else {
        setIdCards([]);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch ID cards";
      setError(errorMessage);
      toast.error(errorMessage);
      setIdCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdCards();
  }, []);

  const filteredIdCards = idCards.filter(card => {
    const term = searchTerm.toLowerCase();
    return (
      (card.name && card.name.toLowerCase().includes(term)) ||
      (card.email && card.email.toLowerCase().includes(term)) ||
      (card.mobile && card.mobile.toLowerCase().includes(term)) ||
      (card.cityState && card.cityState.toLowerCase().includes(term)) ||
      (card.state && card.state.toLowerCase().includes(term)) ||
      (card.city && card.city.toLowerCase().includes(term))
    );
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredIdCards.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredIdCards.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans">
      <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-amber-600 z-50"></div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-amber-500/20 ring-4 ring-amber-50">
            <FaClock size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-[#1e293b] tracking-tight">
              Awaiting Audit
            </h1>
            <p className="text-[#94a3b8] text-xs font-bold tracking-tight mt-1 uppercase tracking-widest flex items-center gap-2">
              {idCards.length} credential requests pending approval
            </p>
          </div>
        </div>
        <button
          onClick={fetchIdCards}
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all font-semibold active:scale-95"
        >
          <FaRedo className={`${loading ? 'animate-spin' : ''}`} />
          Refresh Requests
        </button>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-amber-500/5 border border-[#f1f5f9] overflow-hidden">
        <div className="p-6 md:p-8 border-b border-[#f1f5f9] flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center text-lg shadow-sm">
              <FaSearch />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1e293b]">Audit Directory</h3>
              <p className="text-xs text-[#94a3b8] font-bold uppercase tracking-tighter">Verified Credentials Dept.</p>
            </div>
          </div>
          <div className="relative w-full md:w-96 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-amber-500 transition-colors" />
            <input
              type="text"
              placeholder="Filter by applicant name or contact..."
              className="w-full pl-12 pr-4 py-3.5 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-amber-500/10 focus:bg-white focus:border-amber-200 transition-all text-sm font-medium text-[#334155]"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="px-8 py-5 text-left text-[10px] font-black text-[#64748b] uppercase tracking-widest bg-[#f8fafc]/50">#</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest bg-[#f8fafc]/50">Applicant Profile</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Metadata</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Communication Axis</th>
                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Social Footprint</th>
                <th className="px-8 py-5 text-center text-xs font-black text-[#64748b] uppercase tracking-widest">Status/Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="5" className="px-8 py-8"><div className="h-10 bg-gray-100 rounded-2xl w-full"></div></td>
                  </tr>
                ))
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-8 py-20 text-center text-[#94a3b8] font-black uppercase tracking-widest">No pending audits found</td>
                </tr>
              ) : (
                currentItems.map((card, idx) => (
                  <tr key={card._id} className="hover:bg-amber-50/5 transition-all duration-200 group">
                    <td className="px-8 py-3 whitespace-nowrap text-[10px] font-black text-gray-400">
                      {(currentPage - 1) * itemsPerPage + idx + 1}
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          {card.image ? (
                            <img src={card.image} alt={card.name} className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-xl shadow-amber-500/10 group-hover:scale-105 transition-transform duration-300" />
                          ) : (
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center text-white text-xl font-black"><FaIdCard /></div>
                          )}
                        </div>
                        <div>
                          <p className="text-base font-black text-[#1e293b] uppercase tracking-tight">{card.name || "Anonymous Applicant"}</p>
                          <p className="text-[10px] text-[#94a3b8] font-extrabold uppercase mt-0.5 tracking-wider">{card.designation || "Press Correspondent"}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col gap-1.5">
                        {card.bloodGroup && (
                          <span className="px-2 py-1 bg-red-50 text-red-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-red-100 w-fit">
                            <FaTint className="inline mr-1" size={8} /> {card.bloodGroup}_POSITIVE
                          </span>
                        )}
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Requested: {new Date(card.createdAt).toLocaleDateString()}</p>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{card.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-gray-300 text-[10px]" />
                          <span className="text-sm font-bold text-[#475569]">{card.mobileNo || 'N/A'}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {card.channelLinks && card.channelLinks.length > 0 ? (
                          card.channelLinks.slice(0, 2).map((link, i) => (
                            <a key={i} href={link.link} target="_blank" className="px-2 py-0.5 bg-blue-50 text-blue-500 rounded-md flex items-center gap-1 hover:bg-blue-600 hover:text-white transition-all border border-blue-100 text-[7px] font-black uppercase">
                              <FaLink size={7} /> {link.platform ? `${link.platform} Link` : "Platform Link"}
                            </a>
                          ))
                        ) : <span className="text-[8px] text-gray-300 font-black uppercase tracking-widest text-center w-full">Offline Hub</span>}
                      </div>
                    </td>

                    <td className="px-8 py-6 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center gap-3">
                        <span className="px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest bg-amber-50 text-amber-600 border border-amber-100 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div> REVIEW_REQUIRED
                        </span>
                        <div className="flex gap-2">
                          <button onClick={() => toast.info("Audit system processing...")} className="w-9 h-9 flex items-center justify-center bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-xl transition-all border border-emerald-100 active:scale-90"><FaCheckCircle size={14} /></button>
                          <button onClick={() => toast.info("Audit system processing...")} className="w-9 h-9 flex items-center justify-center bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-xl transition-all border border-red-100 active:scale-90"><FaTimesCircle size={14} /></button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-8 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-black uppercase tracking-widest text-[#94a3b8]">
            <p>Queue Index {currentPage} of {totalPages}</p>
            <div className="flex items-center gap-2 bg-white p-1 rounded-2xl border border-gray-100">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30"><FaChevronLeft size={10} /></button>
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30"><FaChevronRight size={10} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingIdCards;